import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAuthSubscription, getCurrentUser } from '@/lib/api/auth';
import {
  SUBSCRIPTION_ACCESS_BLOCKED_EVENT,
  readSubscriptionGateState,
} from '@/lib/subscriptionGate';
import { SubscriptionRequiredModal } from '@/components/auth/SubscriptionRequiredModal';
import { isDemoModeEnabled } from '@/demo/config';

const PUBLIC_PATH_PREFIXES = [
  '/login',
  '/signup',
  '/forgot-password',
  '/client-consent',
  '/questionnaires/share/',
  '/book/',
  '/reschedule/',
];

const shouldRefreshSubscriptionStatus = (pathname: string): boolean => {
  if (PUBLIC_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix))) {
    return false;
  }

  return true;
};

const isSubscriptionAccessBlocked = (): boolean =>
  Boolean(readSubscriptionGateState()?.accessBlocked);

export const SubscriptionGateBridge = () => {
  const location = useLocation();
  const [modalOpen, setModalOpen] = useState(isSubscriptionAccessBlocked);
  const isDemoMode = isDemoModeEnabled();

  useEffect(() => {
    if (isDemoMode) return;
    const handleSubscriptionBlocked = () => {
      if (isSubscriptionAccessBlocked()) {
        setModalOpen(true);
      }
    };

    window.addEventListener(SUBSCRIPTION_ACCESS_BLOCKED_EVENT, handleSubscriptionBlocked);
    return () => {
      window.removeEventListener(SUBSCRIPTION_ACCESS_BLOCKED_EVENT, handleSubscriptionBlocked);
    };
  }, [isDemoMode]);

  useEffect(() => {
    if (isDemoMode) return;
    setModalOpen(isSubscriptionAccessBlocked());
  }, [isDemoMode, location.pathname, location.search]);

  useEffect(() => {
    if (isDemoMode) return;
    const currentUser = getCurrentUser();
    if (!currentUser?.token || !shouldRefreshSubscriptionStatus(location.pathname)) {
      return;
    }

    let isCancelled = false;

    const refreshSubscriptionStatus = async () => {
      try {
        await getAuthSubscription();
        if (isCancelled) {
          return;
        }

        setModalOpen(isSubscriptionAccessBlocked());
      } catch {
        // Protected API failures are handled by the HTTP interceptor.
      }
    };

    void refreshSubscriptionStatus();

    return () => {
      isCancelled = true;
    };
  }, [isDemoMode, location.pathname, location.search]);

  if (isDemoMode) {
    return null;
  }

  return (
    <SubscriptionRequiredModal open={modalOpen} />
  );
};
